






























import { Component, Vue } from "vue-property-decorator";

import BreadcrumbList, {
  BreadcrumbData,
} from "@/components/common/breadcrumb/BreadcrumbList.vue";
import FlexibleContentArea from "@/components/layout/FlexibleContentArea.vue";
import SiteSimpleImageArea from "@/components/layout/SiteSimpleImageArea.vue";
import ServiceCategoryTab from "@/components/service/ServiceCategoryTab.vue";
import ServiceGroupItem from "@/components/service/ServiceGroupItem.vue";
import ServiceStatusUpdateHeader from "@/components/service-status/ServiceStatusUpdateHeader.vue";
import StatusExplanationHeader from "@/components/service-status/StatusExplanationHeader.vue";
import { API_ERROR_MESSAGE, ServiceMenuType } from "@/consts/const";
import { RouteName } from "@/consts/route";
import { BaseApiError } from "@/errors/api/base-api-error";
import { ServiceCategoryInterface } from "@/interfaces/service-category.interface";
import { ServiceGroupInterface } from "@/interfaces/service-group.interface";
import { ServiceMenuInterface } from "@/interfaces/service-menu.interface";
import { ViewUtil } from "@/utils/view.util";
import { WindowManager } from "@/view/window-manager";

@Component({
  components: {
    SiteSimpleImageArea,
    ServiceGroupItem,
    ServiceStatusUpdateHeader,
    StatusExplanationHeader,
    FlexibleContentArea,
    ServiceCategoryTab,
    BreadcrumbList,
  },
})
export default class ServiceStatusPage extends Vue {
  private updatedTime = new Date().getTime();
  private loading = false;
  private categories: ServiceCategoryInterface[] = [];
  private category: ServiceCategoryInterface | null = null;
  private serviceGroups: ServiceGroupInterface[] = [];
  private isFirstLoading = true;
  private windowManager!: WindowManager;

  async mounted(): Promise<void> {
    try {
      const categories = await this.$service.getCategories(this);
      this.showLoading();
      const category = this.$service.getCategoryByRouteKey(
        categories,
        this.$route.params.key
      );

      if (!category) {
        this.hideLoading();
        await this.$router.push({ name: RouteName.NotFound });
        return;
      }

      this.windowManager = new WindowManager(this.$device);
      this.categories = categories;
      this.category = category;
      await this.loadServices();
    } catch (e) {
      console.log(e);
      if (this.category) {
        this.errorToast(this.$t(API_ERROR_MESSAGE.FAILED_TO_GET).toString());
      } else {
        this.errorToast(this.$t(API_ERROR_MESSAGE.FAILED_GENERAL).toString());
      }
    } finally {
      if (this.category) {
        this.hideLoading();
      }
    }
  }

  private get breadcrumbs(): BreadcrumbData[] {
    if (!this.category) {
      return [{ name: "Home", route: RouteName.Home }];
    }

    return [
      { name: "Home", route: RouteName.Home },
      {
        name: this.category.name,
        route: RouteName.Service,
        params: { key: this.category.routeKey },
      },
      {
        name: this.$t(
          "message.ServiceStatusPage.breadcrumbs_service_status_page_status"
        ).toString(),
      },
    ];
  }

  private loadServices(): Promise<void> {
    return this.$api
      .getServicesInServiceCategory(
        (this.category as ServiceCategoryInterface).id
      )
      .then((serviceGroups: ServiceGroupInterface[]) => {
        this.serviceGroups = serviceGroups;

        if (this.isFirstLoading && this.$route.hash !== "") {
          this.$nextTick(() => {
            const elem: HTMLElement | null = this.$el.querySelector(
              this.$route.hash
            );

            if (elem) {
              window.scrollTo(
                0,
                elem.offsetTop + this.getOffsetDelta(this.$route.hash)
              );
            }
          });
        }

        this.isFirstLoading = false;
      })
      .catch((e: BaseApiError) => {
        throw e;
      });
  }

  private getOffsetDelta(hash: string): number {
    const isPc = this.windowManager.getActualInnerWidth() > 767;

    if (/^#service-[0-9]+$/.test(hash)) {
      return isPc ? -92 : -78;
    } else if (/^#service-menu-[0-9]+$/.test(hash)) {
      return isPc ? -72 : -58;
    } else {
      return 0;
    }
  }

  private get categoryId(): number {
    if (!this.category) {
      return 0;
    }

    return this.category.id;
  }

  private onUpdate(): void {
    if (!this.category) {
      return;
    }

    this.loading = true;
    this.showLoading();
    this.loadServices()
      .then(() => {
        this.updatedTime = new Date().getTime();
      })
      .catch((e) => {
        console.log(e);
        this.errorToast(this.$t(API_ERROR_MESSAGE.FAILED_TO_GET).toString());
      })
      .finally(() => {
        this.loading = false;
        this.hideLoading();
      });
  }

  private onClickTab(category: ServiceCategoryInterface): void {
    this.category = category;
    this.serviceGroups = [];
    this.$router.push({
      name: RouteName.Service,
      params: { key: category.routeKey },
    });
  }

  private onClickServiceMenu(serviceMenu: ServiceMenuInterface): void {
    if (serviceMenu.type === ServiceMenuType.PortalSite) {
      ViewUtil.openInOtherTab(serviceMenu.portalUrl);
    } else if (this.category) {
      this.$router.push({
        name: RouteName.EventList,
        params: {
          key: this.category.routeKey,
          serviceMenuId: `${serviceMenu.id}`,
        },
      });
    }
  }
}
